import React from "react";
import { Table, Tag, Space, Button, Tooltip, Popconfirm } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const STATUS_TAGS = {
  Active: { color: "green", label: "Active" },
  Inactive: { color: "gold", label: "Inactive" },
  Archived: { color: "default", label: "Archived" },
};

const getStatus = (record) =>
  record.status || (record.isActive === false ? "Inactive" : "Active");

const HolidayPackageTable = ({
  packages,
  loading,
  onView,
  onEdit,
  onActivate,
  onDeactivate,
  onArchive,
  onDelete,
}) => {
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
      render: (text) => text,
    },
    {
      title: "Class",
      dataIndex: "className",
      key: "className",
      sorter: (a, b) => (a.className || "").localeCompare(b.className || ""),
    },
    {
      title: "Subject",
      dataIndex: "subjectName",
      key: "subjectName",
      render: (text) => text || <Tag color="cyan">Preschool</Tag>,
    },
    {
      title: "Term",
      dataIndex: "term",
      key: "term",
      render: (text) => text || "-",
    },
    {
      title: "Academic Year",
      dataIndex: "academicYear",
      key: "academicYear",
      sorter: (a, b) =>
        (a.academicYear || "").localeCompare(b.academicYear || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      filters: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
        { text: "Archived", value: "Archived" },
      ],
      onFilter: (value, record) => getStatus(record) === value,
      render: (_, record) => {
        const status = getStatus(record);
        const tag = STATUS_TAGS[status] || {
          color: "default",
          label: status || "Unknown",
        };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (text) => text || "-",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => {
        const status = getStatus(record);
        const isArchived = status === "Archived";
        return (
          <Space size="small">
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => onView(record)}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
            {status !== "Active" && (
              <Tooltip title={isArchived ? "Restore as Active" : "Activate"}>
                <Button
                  type="text"
                  style={{ color: "#52c41a" }}
                  icon={<CheckCircleOutlined />}
                  onClick={() => onActivate(record)}
                />
              </Tooltip>
            )}
            {status !== "Inactive" && (
              <Tooltip
                title={isArchived ? "Restore as Inactive" : "Deactivate"}
              >
                <Popconfirm
                  title={
                    isArchived
                      ? "Restore as Inactive?"
                      : "Deactivate this package?"
                  }
                  description={
                    isArchived
                      ? "This will set the package to Inactive."
                      : "It will be hidden from the default view."
                  }
                  onConfirm={() => onDeactivate(record)}
                  okText="Confirm"
                  cancelText="Cancel"
                >
                  <Button
                    type="text"
                    style={{ color: "#faad14" }}
                    icon={<PauseCircleOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            )}
            {!isArchived && (
              <Tooltip title="Archive">
                <Popconfirm
                  title="Archive this package?"
                  description="It will no longer appear in active or inactive lists."
                  onConfirm={() => onArchive(record)}
                  okText="Archive"
                  cancelText="Cancel"
                >
                  <Button
                    type="text"
                    style={{ color: "#8c8c8c" }}
                    icon={<InboxOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <Popconfirm
                title="Permanently delete this holiday package?"
                description="This cannot be undone."
                onConfirm={() => onDelete(record)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={packages}
      loading={loading}
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      scroll={{ x: 900 }}
      size="small"
    />
  );
};

export default HolidayPackageTable;
